/**
 * Phone numbers. The CRM stores every number in E.164 ("+14074461953") and nothing else, because
 * that is the form Amazon Connect reports a caller in: a screen pop is an exact-match lookup, not
 * a fuzzy one. Normalize on the way in, format on the way out, and never in between.
 *
 * `toE164` matches the desk's own (tomorrow-life-phone-system, src/connect/format.ts) rule for
 * rule, so a number the desk dials and a number the CRM files are the same string.
 */

const US_E164 = /^\+1(\d{3})(\d{3})(\d{4})$/;

/**
 * Normalize typed or imported digits to E.164. US numbers are the default:
 *   "4074461953"        → "+14074461953"
 *   "(407) 446-1953"    → "+14074461953"
 *   "1 407 446 1953"    → "+14074461953"
 *   "+44 20 7946 0958"  → "+442079460958"
 * Returns null when the input cannot be a dialable number.
 */
export function toE164(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  // Letters (vanity numbers, "x123" extensions) and non-ASCII digits are rejected rather than
  // silently stripped, which could otherwise produce a different, valid-looking number.
  if (/\p{L}/u.test(trimmed) || /\p{Nd}/u.test(trimmed.replace(/[0-9]/g, ""))) return null;
  const international = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (international) {
    if (digits.startsWith("1")) {
      return digits.length === 11 && isUsNational(digits.slice(1)) ? `+${digits}` : null;
    }
    if (digits.length < 8 || digits.length > 15 || digits.startsWith("0")) return null;
    return `+${digits}`;
  }
  if (digits.length === 10 && isUsNational(digits)) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1") && isUsNational(digits.slice(1))) {
    return `+${digits}`;
  }
  return null;
}

/** NANP: area code and exchange each start with 2–9. */
function isUsNational(ten: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(ten);
}

/** "+14075550188" → "(407) 555-0188", the desk's layout. Anything else is returned as given. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "";
  const match = US_E164.exec(value.trim());
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  const digits = value.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return value;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/**
 * Whether a search string is somebody looking for a number rather than a name: at least four
 * digits and nothing but phone punctuation. "1953" finds every number ending 1953; "Okafor 1953"
 * is a name search.
 */
export function looksLikePhoneQuery(query: string): boolean {
  const q = query.trim();
  return /^[\d\s()+.-]+$/.test(q) && q.replace(/\D/g, "").length >= 4;
}
